import React, { useEffect, useState } from "react";
import { RootStateOrAny, useSelector } from "react-redux";
import { Button, DatePicker, Form, Row, Tooltip } from "antd";
import moment from "moment";
import { v4 as uuid } from "uuid";
import ToggleableVersionsTable from "../ToggleableVersionsTable/ToggleableVersionsTable";
import { toDisplayFormat, toHttpFormat, toHttpFormatStartOfDay } from "../../lib/utils/formatDate";
import versionsTableUtils from "../../lib/utils/versionsTableUtils";
import { useAccountInfoContainerContext } from "../../containers/AccountInfoContainer/AccountInfoContainerContext";
import { hasAccess } from "../RolesModule/hasAccess";
import { MENU_ACCOUNT_ACCOUNTINFO_WRITE } from "../../lib/const/permissions";
import ChangeVersionsTable from "../common/ChangeVersionsTable";
import { checkIsLoaded, checkIsLoading } from "../../lib/utils/checkLoadingStatus";
import { ReactComponent as AddVersionIcon } from "../../img/versions_icons/addVersion_icon.svg";
import { displayFormat } from "../../lib/utils";

const IndividualInfoVersionsTable = () => {
  const {
    accountStatus,
    accountStatusLoading,
    accountsVersions,
    accountsVersionsLoading,
  } = useSelector((state: RootStateOrAny) => state.accounts);

  const { accountStatuses, accountTypes } = useSelector((state: RootStateOrAny) => state.dicts);
  const [tableData, setTableData] = useState<any>([]);
  const [childTableData, setChildTableData] = useState<any>([]);
  
  const [form] = Form.useForm();

  const isChangingForbidden = versionsTableUtils.getIsTableChanging(childTableData);

  const handleSetInitialValue = (field) => {
    setTimeout(() => {
      form.setFieldsValue(field);
    }, 0);
  };

  const { changeAccountVersion, getAccountVersions } = useAccountInfoContainerContext();

  useEffect(() => {
    if (checkIsLoaded(accountStatusLoading)) {
      setTableData([
        {
          key: accountStatus.id,
          id: accountStatus.id,
          accountStatusId: accountStatus.accountStatusId,
          fromDate: accountStatus.fromDate,
          toDate: accountStatus.toDate,
          accTypeId: accountStatus.accTypeId,
        },
      ]);
    }
  }, [accountStatuses, accountStatusLoading, accountTypes]);

  useEffect(() => {
    if (checkIsLoaded(accountsVersionsLoading)) {
      const childData = accountsVersions[0]
        .map((version, i) => ({
          ...version,
          ...versionsTableUtils.getCommonChildTableProps(version, i, accountsVersions[0].length),
        }))
        .sort(versionsTableUtils.sortVersionsByDate);
      setChildTableData(childData);
    }
  }, [accountsVersionsLoading, accountStatuses, accountTypes]);

  const columnsWidths = {
    ...versionsTableUtils.columnsWidths,
    accountStatusId: 400,
    accTypeId: "",
  };

  const [columns, setColumns] = useState([
    {
      title: "Статус",
      dataIndex: "accountStatusId",
      key: "accountStatusId",
      width: columnsWidths.accountStatusId,
      hidden: false,
      render: (accountStatusId) =>
        versionsTableUtils.geDicValueById(accountStatuses, accountStatusId)?.name,
    },
    {
      title: "Тип аккаунта",
      dataIndex: "accTypeId",
      key: "accTypeId",
      width: columnsWidths.accTypeId,
      hidden: false,
      render: (accTypeId) => versionsTableUtils.geDicValueById(accountTypes, accTypeId)?.name,
    },
    {
      title: "Период действия",
      key: "period",
      dataIndex: "period",
      width: columnsWidths.period,
      hidden: false,
      render(text, record) {
        return `${toDisplayFormat(record.fromDate)} - ${toDisplayFormat(record.toDate)}`;
      },
    },
    {
      dataIndex: "actions",
      key: "actions",
      width: columnsWidths.actions,
      hidden: false,
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      hidden: false,
    },
  ]);

  const getChildColumns = () => [
    {
      title: "Статус",
      dataIndex: "accountStatusId",
      key: "accountStatusId",
      width: columnsWidths.accountStatusId,
      hidden: false,
      render: (value, record) => (
        <ChangeVersionsTable.Select
          dic={accountStatuses}
          name="accountStatusId"
          record={record}
          setFieldsValue={form.setFieldsValue}
          required
          presetAddVerionValue
        />
      ),
    },
    {
      title: "Тип аккаунта",
      dataIndex: "accTypeId",
      key: "accTypeId",
      width: columnsWidths.accTypeId,
      hidden: false,
      render: (value, record) => (
        <ChangeVersionsTable.Select
          dic={accountTypes}
          name="accTypeId"
          record={record}
          setFieldsValue={form.setFieldsValue}
          required
          presetAddVerionValue
        />
      ),
    },
    {
      title: "Период действия",
      key: "period",
      dataIndex: "period",
      width: columnsWidths.period,
      hidden: false,
      render(text, record) {
        if (record.isCreated) {
          handleSetInitialValue({ fromDate: moment(record.fromDate).add(1, "day") });
          return (
            <>
              <Form.Item
                name="fromDate"
                className="form-item-required__hidden-error__mb0"
                rules={[{ required: true }]}
                style={{ display: "inline-block", marginRight: "5px" }}
              >
                <DatePicker
                  format={displayFormat}
                  allowClear={false}
                  disabledDate={(current) =>
                    current < moment(record.fromDate) || current > moment(record.toDate)
                  }
                  // defaultPickerValue={moment(record.fromDate).add(1, "day")}
                />
              </Form.Item>
              <Form.Item
                style={{ display: "inline-block" }}
                className="form-item-required__hidden-error__mb0"
              >
                -{toDisplayFormat(record.toDate)}
              </Form.Item>
            </>
          );
        }
        if (record.isClosing) {
          handleSetInitialValue({ toDate: moment(record.toDate) });
          return (
            <>
              <Form.Item
                style={{ display: "inline-block", marginRight: "5px" }}
                className="form-item-required__hidden-error__mb0"
              >
                {toDisplayFormat(record.fromDate)}-
              </Form.Item>
              <Form.Item
                name="toDate"
                className="form-item-required__hidden-error__mb0"
                rules={[{ required: true }]}
                style={{ display: "inline-block" }}
              >
                <DatePicker
                  format={displayFormat}
                  allowClear={false}
                  disabledDate={(current) => current < moment().startOf("month")}
                  // defaultPickerValue={moment(record.toDate)}
                />
              </Form.Item>
            </>
          );
        }
        if (record.fromDate && record.toDate) {
          return `${toDisplayFormat(record.fromDate)} - ${toDisplayFormat(record.toDate)}`;
        }
        return "";
      },
    },
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      width: columnsWidths.actions,
      hidden: false,
      render: (value, record) => {
        const isHasAccess = hasAccess(MENU_ACCOUNT_ACCOUNTINFO_WRITE);
        // if (
        //   hasAccess &&
        //   !record.isPastVersion &&
        //   !versionsTableUtils.getIsRecordChanging(record) &&
        //   !moment(record.fromDate).isSame(record.toDate, "day")
        // ) {
        //   const addVersion = (e) => {
        //     e.stopPropagation();
        //     const newVersion = {
        //       ...record,
        //       key: uuid(),
        //       fromDate: moment(record.fromDate).add(1, "seconds"),
        //       isCreated: true,
        //       isCurrentVersion: false,
        //       isPastVersion: false,
        //       isLatestVersion: false,
        //     };

        //     setChildTableData((prevState) =>
        //       [newVersion, ...prevState].sort(versionsTableUtils.sortVersionsByDate)
        //     );
        //   };

        //   return (
        //     <Row>
        //       {!moment(record.fromDate)
        //         .startOf("day")
        //         .isSame(moment(record.toDate).startOf("day")) && (
        //         <Tooltip title="Добавить версию">
        //           <Button
        //             className="button-icon"
        //             icon={<AddVersionIcon />}
        //             type="text"
        //             htmlType="button"
        //             disabled={isChangingForbidden}
        //             onClick={addVersion}
        //           />
        //         </Tooltip>
        //       )}
        //     </Row>
        //   );
        // }

        return (
          <>
            <ChangeVersionsTable.AddButton
              hasAccess={isHasAccess}
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            <ChangeVersionsTable.SubmitButton record={record} submit={form.submit} />
            <ChangeVersionsTable.CancelButton
              record={record}
              resetFields={form.resetFields}
              setChildTableData={setChildTableData}
            />
          </>
        );
      },
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      hidden: false,
    },
  ];

  const [childColumns, setChildColumns] = useState(getChildColumns());

  useEffect(() => {
    setChildColumns(getChildColumns());
  }, [isChangingForbidden]);

  const onFinish = ({ fromDate, accountStatusId, accTypeId }) => {
    const createdVersion = versionsTableUtils.getChangeableRecord(childTableData);
    if (createdVersion.isCreated) {
      const date = fromDate.isSame(createdVersion.fromDate, "day")
        ? toHttpFormat(moment(createdVersion.fromDate).add(1, "second"))
        : toHttpFormatStartOfDay(fromDate);

      changeAccountVersion(accountStatusId, date, accTypeId);
    }
  };

  return (
    <Form form={form} onFinish={onFinish}>
      <h4 style={{ marginTop: "45px" }}>Информация об аккаунте</h4>
      <ToggleableVersionsTable
        tableName="individual-status-versions-table"
        columns={columns}
        setColumns={setColumns}
        childTableColumns={childColumns}
        setChildTableColumns={setChildColumns}
        tableData={tableData}
        childTableData={childTableData}
        isChildTableLoading={checkIsLoading(accountsVersionsLoading)}
        tableClassName="individual-status-versions-table"
        isPageable={false}
        isToggleableColumns={false}
        onOpenVersionsCallback={() => {
          getAccountVersions();
        }}
      />
    </Form>
  );
};

export default IndividualInfoVersionsTable;
